import React, { useState } from 'react';
import { Time, Board } from '@vibe/icons';
import { useMondayContext } from '../context/MondayContext';
import SchedulerBoard from '../components/scheduler/SchedulerBoard';
import TimeTrackingWorkspace from '../components/timetracking/TimeTrackingWorkspace';

// ─── Types ─────────────────────────────────────────────────────────────────────

type BoardViewTab = 'worklog' | 'teamboard';

interface TabDef {
  key: BoardViewTab;
  label: string;
  icon: React.FC<{ size?: string | number }>;
}

const TABS: TabDef[] = [
  { key: 'worklog', label: 'Worklog', icon: Time },
  { key: 'teamboard', label: 'Resource Planning', icon: Board },
];

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * Board View surface entry point.
 *
 * Rendered inside a monday.com board view iframe. Has access to the current
 * boardId and provides tab-based navigation between:
 * - **Worklog** — Time tracking workspace
 * - **Resource Planning** — Resource scheduler (Gantt-like timeline)
 */
const BoardView: React.FC = () => {
  const { context, isReady, isAdmin } = useMondayContext();
  const [activeTab, setActiveTab] = useState<BoardViewTab>('teamboard');

  if (!isReady) {
    return (
      <div className="rp-view-loading">
        Loading board context...
      </div>
    );
  }

  if (!context.boardId) {
    return (
      <div className="rp-view-error">
        No board connected. Please add this view to a board.
      </div>
    );
  }

  return (
    <div className="rp-board-view">
      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className="rp-board-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`rp-board-tabs__tab${isActive ? ' rp-board-tabs__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab content ──────────────────────────────────────────────────── */}
      <div className="rp-board-view__content">
        {activeTab === 'teamboard' && (
          <SchedulerBoard boardId={context.boardId} />
        )}
        {activeTab === 'worklog' && (
          <TimeTrackingWorkspace
            currentUserId={context.userId}
            isAdmin={isAdmin}
          />
        )}
      </div>
    </div>
  );
};

export default BoardView;
